import React, { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export default function OnboardingOverviewTab({ user }) {
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedEmployee, setExpandedEmployee] = useState(null);

  useEffect(() => {
    fetchOverviewData();
  }, []);

  const fetchOverviewData = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/admin/onboarding/overview`, {
        headers: {
          'X-User-Email': user?.email || ''
        },
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to fetch onboarding overview');

      const data = await res.json();
      setOverviewData(data);
    } catch (error) {
      console.error('Error fetching onboarding overview:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleEmployeeExpansion = (employeeEmail) => {
    setExpandedEmployee(expandedEmployee === employeeEmail ? null : employeeEmail);
  };

  const getStatusBadgeColor = (percentage) => {
    if (percentage < 50) return '#dc3545'; // Red - needs attention
    if (percentage < 100) return '#ffc107'; // Yellow - in progress
    return '#28a745'; // Green - complete
  };

  const getStatusText = (percentage) => {
    if (percentage < 50) return 'Needs Attention';
    if (percentage < 100) return 'In Progress';
    return 'Complete';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const generateStatusReport = () => {
    if (!overviewData || !overviewData.employees) return '';

    const { employees, summary } = overviewData;
    const currentDate = new Date().toLocaleDateString();
    
    let report = `ONBOARDING STATUS REPORT - ${currentDate}\n`;
    report += `${'='.repeat(50)}\n\n`;
    
    // Summary section
    report += ` SUMMARY:\n`;
    report += `• Total employees with incomplete tasks: ${summary.total_employees_with_incomplete_tasks || 0}\n`;
    report += `• Total incomplete items: ${summary.total_incomplete_items || 0}\n`;
    report += `• Need immediate attention (<50%): ${summary.needs_attention_count || 0}\n`;
    report += `• In progress (50-99%): ${summary.in_progress_count || 0}\n\n`;

    // Helper function to get first name
    const getFirstName = (fullName, email) => {
      if (fullName && typeof fullName === 'string' && fullName.trim()) {
        const firstName = fullName.trim().split(' ')[0];
        return firstName || 'there';
      }
      if (email && typeof email === 'string') {
        const emailName = email.split('@')[0];
        return emailName || 'there';
      }
      return 'there';
    };

    // Helper function to format task list
    const formatTaskList = (tasks) => {
      if (!tasks || !Array.isArray(tasks)) return '• No tasks found';
      
      return tasks.map(task => {
        const title = task?.title || 'Untitled Task';
        const description = task?.description || 'No description available';
        const link = task?.link ? ` - ${task.link}` : '';
        return `• ${title} - ${description}${link}`;
      }).join('\n');
    };

    // Group employees by priority with safety checks
    const safeEmployees = Array.isArray(employees) ? employees : [];
    const needsAttention = safeEmployees.filter(emp => 
      emp && typeof emp.completion_percentage === 'number' && emp.completion_percentage < 50
    );
    const inProgress = safeEmployees.filter(emp => 
      emp && typeof emp.completion_percentage === 'number' && 
      emp.completion_percentage >= 50 && emp.completion_percentage < 100
    );

    if (needsAttention.length > 0) {
      report += `🔴 IMMEDIATE ACTION REQUIRED:\n`;
      report += `${'='.repeat(30)}\n\n`;
      
      needsAttention.forEach((emp, index) => {
        const firstName = getFirstName(emp?.name, emp?.email || 'unknown');
        const empName = emp?.name || emp?.email || 'Unknown Employee';
        const completedItems = emp?.completed_items || 0;
        const totalItems = emp?.total_items || 0;
        const percentage = emp?.completion_percentage || 0;
        const startDate = emp?.start_date || 'Unknown';
        
        report += `${index + 1}. ${empName}\n`;
        report += `Progress: ${completedItems}/${totalItems} (${Math.round(percentage)}%) | Started: ${formatDate(startDate)}\n\n`;
        
        report += `SUGGESTED MESSAGE:\n`;
        report += `Hi ${firstName}, can you please complete the following open items on your onboarding? Or, if you've done them, be sure to mark them complete in the dashboard.\n\n`;
        report += formatTaskList(emp?.incomplete_items);
        report += `\n\nThanks!\n`;
        report += `${'-'.repeat(40)}\n\n`;
      });
    }

    if (inProgress.length > 0) {
      report += `🟡 FOLLOW-UP NEEDED:\n`;
      report += `${'='.repeat(30)}\n\n`;
      
      inProgress.forEach((emp, index) => {
        const firstName = getFirstName(emp?.name, emp?.email || 'unknown');
        const empName = emp?.name || emp?.email || 'Unknown Employee';
        const completedItems = emp?.completed_items || 0;
        const totalItems = emp?.total_items || 0;
        const percentage = emp?.completion_percentage || 0;
        const startDate = emp?.start_date || 'Unknown';
        
        report += `${index + 1}. ${empName}\n`;
        report += `Progress: ${completedItems}/${totalItems} (${Math.round(percentage)}%) | Started: ${formatDate(startDate)}\n\n`;
        
        report += `SUGGESTED MESSAGE:\n`;
        report += `Hi ${firstName}, you're making great progress on your onboarding! Just a few more items to complete:\n\n`;
        report += formatTaskList(emp?.incomplete_items);
        report += `\n\nLet me know if you need any help with these!\n`;
        report += `${'-'.repeat(40)}\n\n`;
      });
    }

    // Manager summary
    const allManagers = [...new Set([...needsAttention, ...inProgress]
      .map(emp => emp?.manager_email)
      .filter(Boolean))];
    if (allManagers.length > 0) {
      report += ` MANAGER NOTIFICATIONS:\n`;
      report += `${'='.repeat(30)}\n`;
      report += `Consider notifying these managers about their direct reports' onboarding status:\n`;
      allManagers.forEach(manager => {
        const managedEmployees = [...needsAttention, ...inProgress]
          .filter(emp => emp?.manager_email === manager);
        const employeeNames = managedEmployees
          .map(emp => emp?.name || emp?.email || 'Unknown Employee')
          .join(', ');
        report += `• ${manager}: ${employeeNames}\n`;
      });
      report += '\n';
    }
    
    report += `Generated from Symbolica Operations Dashboard\n`;
    report += `Dashboard link: ${window.location.origin}/dashboard (Admin Panel > Reporting > Onboarding Overview)`;
    
    return report;
  };

  const copyStatusReport = async () => {
    const report = generateStatusReport();
    
    try {
      await navigator.clipboard.writeText(report);
      // Show temporary success message
      const button = document.querySelector('.copy-report-button');
      const originalText = button.textContent;
      button.textContent = 'Done Copied!';
      button.style.backgroundColor = '#28a745';
      
      setTimeout(() => {
        button.textContent = originalText;
        button.style.backgroundColor = '';
      }, 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = report;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      // Show success message
      alert('Status report copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="tab-content">
        <div className="tab-header">
          <h2>Onboarding Overview</h2>
        </div>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          Loading onboarding overview...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-content">
        <div className="tab-header">
          <h2>Onboarding Overview</h2>
        </div>
        <div style={{ textAlign: 'center', padding: '40px', color: '#dc3545' }}>
          Error: {error}
        </div>
      </div>
    );
  }

  const { employees, summary } = overviewData || { employees: [], summary: {} };

  return (
    <div className="tab-content">
      <div className="tab-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2>Onboarding Overview</h2>
            <p style={{ color: '#666', marginTop: '8px' }}>
              High-level view of employees with incomplete onboarding tasks
            </p>
          </div>
          <button 
            className="primary-button copy-report-button"
            onClick={copyStatusReport}
            disabled={loading || !overviewData || overviewData.employees.length === 0}
            style={{ 
              marginTop: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
             Copy Status Report
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '16px', 
        marginBottom: '32px' 
      }}>
        <div className="summary-card" style={{
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '24px', color: '#495057' }}>
            {summary.total_employees_with_incomplete_tasks || 0}
          </h3>
          <p style={{ margin: 0, color: '#6c757d' }}>Employees with Tasks</p>
        </div>

        <div className="summary-card" style={{
          padding: '20px',
          backgroundColor: '#fff3cd',
          borderRadius: '8px',
          border: '1px solid #ffeaa7'
        }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '24px', color: '#856404' }}>
            {summary.total_incomplete_items || 0}
          </h3>
          <p style={{ margin: 0, color: '#856404' }}>Total Incomplete Items</p>
        </div>

        <div className="summary-card" style={{
          padding: '20px',
          backgroundColor: '#f8d7da',
          borderRadius: '8px',
          border: '1px solid #f5c6cb'
        }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '24px', color: '#721c24' }}>
            {summary.needs_attention_count || 0}
          </h3>
          <p style={{ margin: 0, color: '#721c24' }}>Need Attention (&lt;50%)</p>
        </div>

        <div className="summary-card" style={{
          padding: '20px',
          backgroundColor: '#d1ecf1',
          borderRadius: '8px',
          border: '1px solid #bee5eb'
        }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '24px', color: '#0c5460' }}>
            {summary.in_progress_count || 0}
          </h3>
          <p style={{ margin: 0, color: '#0c5460' }}>In Progress (50-99%)</p>
        </div>
      </div>

      {/* Employee List */}
      <div className="section-container">
        <h3>Employees with Incomplete Tasks</h3>
        
        {employees.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px', 
            backgroundColor: '#d4edda', 
            borderRadius: '8px',
            border: '1px solid #c3e6cb',
            color: '#155724'
          }}>
             Great! All employees have completed their onboarding tasks.
          </div>
        ) : (
          <div className="employees-list">
            {employees.map((employee) => (
              <div 
                key={employee.email} 
                className="employee-card" 
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  backgroundColor: '#fff'
                }}
              >
                {/* Employee Header */}
                <div 
                  style={{
                    padding: '20px',
                    cursor: 'pointer',
                    borderBottom: expandedEmployee === employee.email ? '1px solid #e0e0e0' : 'none'
                  }}
                  onClick={() => toggleEmployeeExpansion(employee.email)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: '0 0 8px 0' }}>
                        {employee.name || employee.email}
                        <span style={{ 
                          marginLeft: '12px',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 'normal',
                          backgroundColor: getStatusBadgeColor(employee.completion_percentage),
                          color: '#fff'
                        }}>
                          {getStatusText(employee.completion_percentage)}
                        </span>
                      </h4>
                      <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                        {employee.email} • {employee.country}
                        {employee.manager_email && (
                          <span> • Manager: {employee.manager_email}</span>
                        )}
                      </div>
                      <div style={{ fontSize: '14px', color: '#666' }}>
                        Started: {formatDate(employee.start_date)}
                      </div>
                    </div>
                    
                    <div style={{ textAlign: 'right', minWidth: '200px' }}>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>
                        {employee.completed_items} / {employee.total_items}
                      </div>
                      <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                        {Math.round(employee.completion_percentage)}% Complete
                      </div>
                      <div className="progress-bar-container" style={{ width: '150px', height: '8px' }}>
                        <div 
                          className="progress-bar-fill"
                          style={{ 
                            width: `${employee.completion_percentage}%`,
                            backgroundColor: getStatusBadgeColor(employee.completion_percentage),
                            height: '100%',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease'
                          }}
                        />
                      </div>
                    </div>
                    
                    <div style={{ marginLeft: '16px', fontSize: '18px' }}>
                      {expandedEmployee === employee.email ? '▼' : '▶'}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedEmployee === employee.email && (
                  <div style={{ padding: '20px' }}>
                    <h5 style={{ margin: '0 0 16px 0', color: '#495057' }}>
                      Incomplete Tasks ({employee.incomplete_items_count})
                    </h5>
                    
                    {employee.incomplete_items.length === 0 ? (
                      <p style={{ color: '#666', fontStyle: 'italic' }}>
                        No incomplete tasks found.
                      </p>
                    ) : (
                      <div className="incomplete-items-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                        gap: '12px'
                      }}>
                        {employee.incomplete_items.map((item) => (
                          <div 
                            key={item.id}
                            style={{
                              padding: '12px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '6px',
                              border: '1px solid #e9ecef'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                              <div style={{ flex: 1 }}>
                                <h6 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '600' }}>
                                  {item.title}
                                  {item.pinned && (
                                    <span style={{
                                      marginLeft: '8px',
                                      padding: '2px 6px',
                                      backgroundColor: '#007bff',
                                      color: '#fff',
                                      borderRadius: '3px',
                                      fontSize: '10px'
                                    }}>
                                      PINNED
                                    </span>
                                  )}
                                </h6>
                                {item.category && item.category !== 'all' && (
                                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                                    Category: {item.category}
                                  </div>
                                )}
                                {item.updated_at && (
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    Last updated: {formatDate(item.updated_at)}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Refresh Button */}
      <div style={{ marginTop: '32px', textAlign: 'center' }}>
        <button 
          className="secondary-button"
          onClick={fetchOverviewData}
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div>
    </div>
  );
}
